/**
 * 共享时批注：在共享画面上叠一层可画的画布。
 *
 * 老师讲 PPT 想圈个重点，原来只能切回白板 —— 而切过去共享画面就没了。
 *
 * 实现上它就是白板的一页（服务端在共享开始时追加、结束时删掉），所以对象同步、
 * 权限、撤销、录制回放全部复用白板那一套，这里只负责：把那一页画在共享画面上方，
 * 并把指针事件换算成归一化坐标交给 whiteboardStore。
 *
 * 工具只给笔、直线、箭头和橡皮 —— 共享期间要的是圈画，不是排版。
 */

import { useEffect, useRef, useState } from 'react'

import type { Client } from '../lib/client'
import { drawObject } from '../lib/wbRender'
import type { WBPage, WBPoint } from '../lib/whiteboard-types'
import { useWhiteboardStore } from '../stores/whiteboardStore'

/** 批注可用的工具。比白板少：共享时要的是圈画重点，不是排版。 */
const TOOLS = [
  { id: 'pen', label: '画笔' },
  { id: 'line', label: '直线' },
  { id: 'rect', label: '方框' },
  { id: 'eraser', label: '橡皮' },
] as const

const COLORS = ['#ff3b30', '#ffcc00', '#34c759', '#0a84ff', '#ffffff']

interface Props {
  /** 批注层那一页 */
  page: WBPage
  client: Client
  selfId: number
  /** 关掉批注（回到「只看共享」） */
  onClose: () => void
}

export function ShareAnnotate({ page, client, selfId, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })
  const [tool, setTool] = useState<(typeof TOOLS)[number]['id']>('pen')
  const [color, setColor] = useState(COLORS[0])
  const draft = useWhiteboardStore((s) => s.draft)

  // 画布跟满整个共享画面：批注要能画在画面任何位置，不像白板那样固定 4:3。
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect()
      setSize({ w: Math.round(r.width), h: Math.round(r.height) })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // 重绘。批注层的对象与草稿都画在这里。
  useEffect(() => {
    const cv = canvasRef.current
    if (!cv || size.w === 0) return
    cv.width = size.w
    cv.height = size.h
    const ctx = cv.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, size.w, size.h)
    for (const o of page.objects ?? []) drawObject(ctx, o, size.w, size.h)
    if (draft) drawObject(ctx, draft, size.w, size.h)
  }, [page, draft, size])

  const toNorm = (e: React.PointerEvent): WBPoint => {
    const r = e.currentTarget.getBoundingClientRect()
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    }
  }

  const onDown = (e: React.PointerEvent) => {
    const at = toNorm(e)
    const wb = useWhiteboardStore.getState()
    if (tool === 'eraser') {
      wb.eraseAt(client, at, selfId, page.index)
      return
    }
    e.currentTarget.setPointerCapture(e.pointerId)
    wb.setStyle({ lineColor: color, lineWidth: 3 })
    wb.beginDraw(tool, at, selfId, page.index)
  }

  const onMove = (e: React.PointerEvent) => {
    const wb = useWhiteboardStore.getState()
    if (!wb.draft) {
      if (tool === 'eraser' && e.buttons === 1) wb.eraseAt(client, toNorm(e), selfId, page.index)
      return
    }
    wb.extendDraw(toNorm(e))
  }

  const onUp = () => useWhiteboardStore.getState().endDraw(client, page.index)

  return (
    <div className="sa-layer" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        className="sa-canvas"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      />
      <div className="sa-bar" onPointerDown={(e) => e.stopPropagation()}>
        {TOOLS.map((t) => (
          <button key={t.id} className={tool === t.id ? 'on' : ''} onClick={() => setTool(t.id)}>
            {t.label}
          </button>
        ))}
        <span className="sa-sep" />
        {COLORS.map((c) => (
          <button
            key={c}
            className={`sa-color${color === c ? ' on' : ''}`}
            style={{ background: c }}
            aria-label={`颜色 ${c}`}
            onClick={() => setColor(c)}
          />
        ))}
        <span className="sa-sep" />
        <button
          onClick={() => useWhiteboardStore.getState().clearPage(client, page.index)}
          title="清空这一层的批注，不影响共享画面"
        >
          清空
        </button>
        <button className="sa-close" onClick={onClose} title="退出批注，回到只看共享">
          退出批注
        </button>
      </div>
    </div>
  )
}
